import { debounce } from 'lodash';
import { useMemo, useState, useEffect } from 'react';

import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import { Button, IconButton, MenuItem } from '@mui/material';

import Iconify from 'src/components/iconify';

import {
  IIssuedChequeFilters,
  ISSUED_CHEQUE_BANK_OPTIONS,
  ISSUED_CHEQUE_TYPE_OPTIONS,
} from 'src/types/issued-cheque';

type Props = {
  filters: IIssuedChequeFilters;
  onFilters: (name: keyof IIssuedChequeFilters, value: string | null) => void;
  canReset: boolean;
  onReset: VoidFunction;
};

export default function IssuedChequeTableToolbar({ filters, onFilters, canReset, onReset }: Props) {
  // Seeded from the filters so a list restored from the URL shows its search.
  const [input, setInput] = useState(filters.generalSearch);

  const debouncedSearch = useMemo(
    () => debounce((value: string) => onFilters('generalSearch', value), 500),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  // Clearing all filters from outside empties the box too.
  useEffect(() => {
    if (!filters.generalSearch) setInput('');
  }, [filters.generalSearch]);

  const handleSearch = (value: string) => {
    setInput(value);
    debouncedSearch(value);
  };

  return (
    <Stack spacing={2} alignItems="center" direction={{ xs: 'column', md: 'row' }} sx={{ p: 2.5 }}>
      <TextField
        fullWidth
        value={input}
        onChange={(event) => handleSearch(event.target.value)}
        placeholder="Search by payee, cheque number or note..."
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
            </InputAdornment>
          ),
          endAdornment:
            input.length > 0 ? (
              <InputAdornment position="end">
                <IconButton
                  edge="end"
                  onClick={() => {
                    debouncedSearch.cancel();
                    setInput('');
                    onFilters('generalSearch', '');
                  }}
                >
                  <Iconify icon="eva:close-fill" />
                </IconButton>
              </InputAdornment>
            ) : null,
        }}
      />

      <TextField
        select
        label="Bank"
        value={filters.bank ?? ''}
        onChange={(event) => onFilters('bank', event.target.value || null)}
        sx={{ flexShrink: 0, width: { xs: 1, md: 200 } }}
      >
        <MenuItem value="">All</MenuItem>
        {ISSUED_CHEQUE_BANK_OPTIONS.map((bank) => (
          <MenuItem key={bank} value={bank}>
            {bank}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        select
        label="Type"
        value={filters.type ?? ''}
        onChange={(event) => onFilters('type', event.target.value || null)}
        sx={{ flexShrink: 0, width: { xs: 1, md: 180 } }}
      >
        <MenuItem value="">All</MenuItem>
        {ISSUED_CHEQUE_TYPE_OPTIONS.map((type) => (
          <MenuItem key={type} value={type}>
            {type}
          </MenuItem>
        ))}
      </TextField>

      {canReset && (
        <Button
          color="error"
          onClick={onReset}
          variant="text"
          sx={{ textDecoration: 'underline', flexShrink: 0 }}
        >
          Clear
        </Button>
      )}
    </Stack>
  );
}
